import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Design 14b — the Input chassis with a multiline body.
 *
 * The box grows with its content instead of showing a drag handle: a
 * user-resized textarea knocks every field below it out of the form's vertical
 * rhythm, and the height it is dragged to is never saved anywhere. Growth stops
 * at `max-h-64` (256px) and the content scrolls inside from there.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(
  ({ className, onChange, value, defaultValue, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    // `rows` (when a caller sets one) is the floor, not the height: clearing the
    // inline height first lets the browser lay the element out from its own
    // attributes, and that measurement becomes the minimum we grow from.
    //
    // The border has to be added back by hand. Under `border-box` sizing the
    // height we set is a border-box height, while `scrollHeight` is a
    // padding-box one — assigning it straight across leaves the content 2px
    // short and shows a scrollbar well before the 256px cap.
    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      const border = el.offsetHeight - el.clientHeight;
      el.style.height = `${Math.max(el.offsetHeight, el.scrollHeight + border)}px`;
    }, []);

    React.useLayoutEffect(resize, [resize, value, defaultValue]);

    return (
      <textarea
        ref={innerRef}
        value={value}
        defaultValue={defaultValue}
        onChange={(event) => {
          resize();
          onChange?.(event);
        }}
        className={cn(
          "flex min-h-[60px] max-h-64 w-full resize-none overflow-y-auto rounded-lg border border-input bg-card px-3 py-1.5 text-base leading-normal shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
